import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCpu, FiShield, FiZap, FiCode, FiLayers, FiActivity } = FiIcons;

const Product = () => {
  const features = [
    {
      title: "The .hd Compiler",
      description: "Our proprietary compiler transforms natural language clinical intent into high-performance, verifiable medical logic packages.",
      icon: FiCode
    },
    {
      title: "Venice v4 Brains",
      description: "Deep integrated clinical models from Venice AI, fine-tuned for triage accuracy and ICD-10 mapping precision.",
      icon: FiCpu
    },
    {
      title: "HF Clinical Engine",
      description: "Direct integration with Hugging Face clinical models (Mistral-7B, Llama-3 Health) for specialized on-premise inference.",
      icon: FiLayers
    },
    {
      title: "Immutable Audit Log",
      description: "Every decision made by the AI is logged to an encrypted, blockchain-verified audit vault for regulatory compliance.",
      icon: FiShield
    }
  ];

  return (
    <div className="pt-24 pb-20 bg-brand-ivory min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <h1 className="text-5xl font-display font-bold text-brand-indigo mb-6">Software Capabilities</h1>
          <p className="text-xl text-brand-slate max-w-3xl mx-auto leading-relaxed">
            HDAI Software is the world's first unified development environment for clinical AI. From zero-code visual building to high-performance CLI automation.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">
          {features.map((f, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl transition-all"
            >
              <div className="w-12 h-12 bg-brand-mint/20 rounded-2xl flex items-center justify-center mb-6">
                <SafeIcon icon={f.icon} className="text-brand-indigo text-xl" />
              </div>
              <h3 className="text-xl font-bold text-brand-indigo mb-3">{f.title}</h3>
              <p className="text-brand-slate text-sm leading-relaxed">{f.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="bg-brand-indigo rounded-[3rem] p-12 lg:p-20 text-white overflow-hidden relative">
          <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-display font-bold mb-6 text-brand-mint">The Workflow Spectrum</h2>
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                Build workflows your way. Our platform synchronizes changes across all development modes instantly.
              </p>
              <div className="space-y-4">
                {['Zero-Code Visual Builder', 'Hybrid NLP Chat Refinement', 'Low-Code .hd DSL', 'Native CLI & SDK'].map((item, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <SafeIcon icon={FiZap} className="text-brand-mint" />
                    <span className="font-bold">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <pre className="text-xs font-mono text-brand-mint/80 overflow-x-auto">
{`workflow ChestPainTriage {
  @version("1.0.0")
  @jurisdiction("us")

  input: { symptoms: string }

  step 1: {
    let risk = assess(input.symptoms, {
      provider: "venice",
      model: "clinical-v4"
    });
  }

  output: { riskScore: risk.score }
}`}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Product;